import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Dictionary } from 'lodash';
import { CustomField } from 'src/app/core/models/custom-fields.models';
import {
  COMPANY_CUSTOM_FIELDS_GET_LIST_SUCCESS,
  CUSTOM_FIELDS_ADD_SUCCESS,
  CUSTOM_FIELDS_DELETE,
  CUSTOM_FIELDS_UPDATE,
  CUSTOM_FIELDS_UPDATE_SUCCESS,
  CustomFieldsAction,
} from 'src/app/core/store/actions/custom-fields';
import { getSortByNumber } from 'src/app/core/utils/sort-utils';

export type CustomFieldsState = {
  readonly ids: string[];
  readonly customFields: Dictionary<CustomField>;
};

export const initialState: CustomFieldsState = {
  ids: [],
  customFields: {},
};

export function reducer(state = initialState, action: CustomFieldsAction): CustomFieldsState {
  switch (action.type) {
    case COMPANY_CUSTOM_FIELDS_GET_LIST_SUCCESS: {
      const fields = action.payload;
      const list = state.ids.map((id) => state.customFields[id]).concat(fields);
      return {
        ...state,
        ids: Array.from(new Set(list.map((cf) => cf.id))),
        customFields: list.reduce<Dictionary<CustomField>>((acc, cf) => {
          acc[cf.id] = cf;
          return acc;
        }, {}),
      };
    }

    case CUSTOM_FIELDS_ADD_SUCCESS: {
      const field = action.payload;

      const customFields: Dictionary<CustomField> = {
        ...state.customFields,
        [field.id]: field,
      };

      return {
        ...state,
        customFields,
        ids: [...state.ids, field.id],
      };
    }

    case CUSTOM_FIELDS_UPDATE:
    case CUSTOM_FIELDS_UPDATE_SUCCESS: {
      const field = action.payload;

      const customFields: Dictionary<CustomField> = {
        ...state.customFields,
        [field.id]: field,
      };

      return {
        ...state,
        customFields,
      };
    }

    case CUSTOM_FIELDS_DELETE: {
      const id = action.payload;

      const customFields = { ...state.customFields };
      delete customFields[id];

      return {
        ...state,
        customFields,
        ids: state.ids.filter((_id) => _id !== id),
      };
    }

    default:
      return state;
  }
}

const getCustomFieldsIds = (state: CustomFieldsState) => state.ids;
const getCustomFieldsMap = (state: CustomFieldsState) => state.customFields;

export const getFieldsStateSelector = createFeatureSelector<CustomFieldsState>('customFieldsState');
export const getFieldsIdsSelector = createSelector(getFieldsStateSelector, getCustomFieldsIds);
export const getFieldsMapSelector = createSelector(getFieldsStateSelector, getCustomFieldsMap);

export const getFieldsListSelector = createSelector(getFieldsIdsSelector, getFieldsMapSelector, (ids, map) => {
  return ids.map((id) => map[id]).sort(getSortByNumber('orderIndex'));
});

export const getFormFieldsListSelector = createSelector(getFieldsListSelector, (list) => {
  return list.filter((c) => !isCompanyCustomField(c));
});

export const getCompanyFieldsListSelector = createSelector(getFieldsListSelector, (list) => {
  return list.filter((c) => isCompanyCustomField(c));
});

function isCompanyCustomField(cf: CustomField) {
  return cf.isForCompany;
}
